import { useEffect, useRef, useState } from 'react';
import { SelectField } from '@/components/ui/SelectField';
import { Toggle } from '@/components/ui/Toggle';
import { cn } from '@/lib/cn';
import { toNumber } from '@/lib/format';
import { fetchReasons } from '@/api/reasons';
import { useAsyncData } from '@/hooks/useAsyncData';
import type { RefundTiming, ReturnReason } from '@/types';
import { PolicyCard, PolicyRow, TagInput } from './fields';
import type { SettingsSectionProps } from './fields';

const RETURN_WINDOW_FROM_OPTIONS = [
  { value: 'ORDER_DATE', label: 'order date' },
  { value: 'DELIVERY_DATE', label: 'delivery date' },
];

const REFUND_TIMING_CHOICES: { value: RefundTiming; label: string; hint: string }[] = [
  { value: 'INSTANT_APPROVE', label: 'On approval', hint: 'Fastest. Risk of fraud.' },
  { value: 'AFTER_PICKUP', label: 'On pickup', hint: 'Balanced — confirmed by courier.' },
  { value: 'AFTER_QC', label: 'On QC pass', hint: 'Safest. Recommended.' },
];

/** Compact number input with a leading/trailing unit, sized for the right column. */
function UnitInput({
  value,
  onChange,
  prefix,
  suffix,
  label,
}: {
  value: number | undefined;
  onChange: (value: number) => void;
  prefix?: string;
  suffix?: string;
  label?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label ? <label className="min-h-8 text-xs font-medium text-ink-muted">{label}</label> : null}
      <div className="flex h-10 items-center rounded-lg border border-line bg-surface px-3 focus-within:ring-2 focus-within:ring-brand">
        {prefix ? <span className="mr-1 text-sm text-ink-muted">{prefix}</span> : null}
        <input
          type="number"
          inputMode="decimal"
          min={0}
          value={value === undefined ? '' : String(value)}
          onChange={(event) => onChange(Math.max(0, toNumber(event.target.value)))}
          className="w-full min-w-0 bg-transparent text-sm text-ink focus:outline-none"
        />
        {suffix ? <span className="ml-1 text-sm text-ink-muted">{suffix}</span> : null}
      </div>
    </div>
  );
}

/** Dropdown multi-select of the seller's return reasons (active or not), stored as slugs. */
function ReasonMultiSelect({
  values,
  onChange,
}: {
  values: string[];
  onChange: (value: string[]) => void;
}) {
  const { data, isLoading, errorMessage } = useAsyncData<ReturnReason[] | null>(
    (signal) => fetchReasons(signal),
    [],
  );
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const reasons = data ?? [];

  // Drop any selected slugs whose reason has since been deleted — otherwise a
  // stale slug can never be unselected (it's not in the list) and keeps getting
  // sent in the save payload.
  useEffect(() => {
    if (isLoading || !data) {
      return;
    }
    const validValues = values.filter((slug) => reasons.some((reason) => reason.slug === slug));
    if (validValues.length !== values.length) {
      onChange(validValues);
    }
  }, [isLoading, data, values, reasons, onChange]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  function toggle(slug: string) {
    onChange(values.includes(slug) ? values.filter((entry) => entry !== slug) : [...values, slug]);
  }

  const labelBySlug = new Map(reasons.map((reason) => [reason.slug, reason.label]));
  const summary =
    values.length === 0
      ? 'Select reasons…'
      : values.map((slug) => labelBySlug.get(slug) ?? slug).join(', ');

  return (
    <div ref={containerRef} className="relative flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex min-h-10 items-center justify-between gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-left text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
      >
        <span className={cn('flex-1 truncate', values.length === 0 && 'text-ink-subtle')}>{summary}</span>
        <span className="text-ink-subtle">▾</span>
      </button>

      {isOpen ? (
        <div className="max-h-60 overflow-auto rounded-lg border border-line bg-surface p-1 shadow-sm">
          {isLoading ? (
            <p className="px-3 py-2 text-sm text-ink-subtle">Loading reasons…</p>
          ) : errorMessage ? (
            <p className="px-3 py-2 text-sm text-danger">Couldn't load reasons.</p>
          ) : reasons.length === 0 ? (
            <p className="px-3 py-2 text-sm text-ink-subtle">No reasons configured yet.</p>
          ) : (
            reasons.map((reason) => (
              <label
                key={reason.slug}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm text-ink hover:bg-surface-muted"
              >
                <input
                  type="checkbox"
                  checked={values.includes(reason.slug)}
                  onChange={() => toggle(reason.slug)}
                  className="h-4 w-4 rounded border-line text-brand focus:ring-brand"
                />
                <span className="flex-1">{reason.label}</span>
                {!reason.isActive ? (
                  <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-xs text-ink-subtle">Inactive</span>
                ) : null}
              </label>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export function PolicySection({ settings, onChange }: SettingsSectionProps) {
  return (
    <div className="flex flex-col gap-5">
      <PolicyCard
        title="Default return policy"
        description="Applies to all orders unless overridden by a regional or product rule."
      >
        <PolicyRow title="Return window" description="How long after delivery customers can request a return.">
          <div className="flex items-center gap-2">
            <div className="w-24">
              <UnitInput
                value={settings.returnWindowDays}
                onChange={(value) => onChange({ returnWindowDays: value })}
              />
            </div>
            <span className="whitespace-nowrap text-sm text-ink-muted">days from</span>
            <SelectField
              className="flex-1"
              value={settings.returnWindowFrom ?? 'ORDER_DATE'}
              onChange={(event) =>
                onChange({ returnWindowFrom: event.target.value as 'ORDER_DATE' | 'DELIVERY_DATE' })
              }
              options={RETURN_WINDOW_FROM_OPTIONS}
            />
          </div>
        </PolicyRow>

        <PolicyRow title="Exchange window" description="Independent from return window. Often set longer.">
          <div className="flex items-center gap-2">
            <div className="w-24">
              <UnitInput
                value={settings.exchangeWindowDays}
                onChange={(value) => onChange({ exchangeWindowDays: value })}
              />
            </div>
            <span className="whitespace-nowrap text-sm text-ink-muted">days from</span>
            <SelectField
              className="flex-1"
              value={settings.exchangeWindowFrom ?? 'ORDER_DATE'}
              onChange={(event) =>
                onChange({ exchangeWindowFrom: event.target.value as 'ORDER_DATE' | 'DELIVERY_DATE' })
              }
              options={RETURN_WINDOW_FROM_OPTIONS}
            />
          </div>
        </PolicyRow>

        <PolicyRow title="Refund timing" description="When the customer's money or credit is released.">
          <div className="flex flex-col gap-2">
            {REFUND_TIMING_CHOICES.map((choice) => {
              const isSelected = settings.refundTiming === choice.value;
              return (
                <button
                  key={choice.value}
                  type="button"
                  onClick={() => onChange({ refundTiming: choice.value })}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                    isSelected ? 'border-brand bg-brand/5' : 'border-line hover:border-ink-subtle/50',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border',
                      isSelected ? 'border-brand' : 'border-ink-subtle',
                    )}
                  >
                    {isSelected ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-ink">{choice.label}</span>
                    <span className="text-xs text-ink-muted">{choice.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </PolicyRow>

        <PolicyRow title="Return fees" description="Deducted from the customer's refund.">
          <div className="grid grid-cols-2 gap-3">
            <UnitInput
              label="Restocking fee"
              suffix="%"
              value={settings.restockingFeePct}
              onChange={(value) => onChange({ restockingFeePct: value })}
            />
            <UnitInput
              label="Return shipping fee"
              prefix="₹"
              value={settings.returnShippingFee}
              onChange={(value) => onChange({ returnShippingFee: value })}
            />
          </div>
        </PolicyRow>

        <PolicyRow title="Pickup address editable" description="Customers can edit the pickup address before placing return request.">
          <div className="sm:self-end">
            <Toggle checked={settings.editPickupAddress} onChange={(checked) => onChange({ editPickupAddress: checked })} />
          </div>
        </PolicyRow>

        <PolicyRow title="Non-returnable items" description="Customers can never start returns on these.">
          <div className="flex flex-col gap-2">
            <TagInput
              values={settings.nonReturnableCategories ?? []}
              onChange={(value) => onChange({ nonReturnableCategories: value })}
              placeholder="Add product types (comma-separated)"
            />
            <TagInput
              values={settings.nonReturnableSkuPatterns ?? []}
              onChange={(value) => onChange({ nonReturnableSkuPatterns: value })}
              placeholder="Add SKU prefixes (comma-separated)"
              transform={(value) => `${value}`}
            />
            <TagInput
              values={settings.nonReturnableCollectionIds ?? []}
              onChange={(value) => onChange({ nonReturnableCollectionIds: value })}
              placeholder="Add collection IDs (comma-separated)"
            />
          </div>
        </PolicyRow>

        <PolicyRow title="Per-customer limits" description="Caps to prevent return abuse.">
          <div className="mt-1 grid grid-cols-2 items-start gap-3">
            <UnitInput
              label="Max items per return request"
              value={settings.maxItemsPerReturnRequest}
              onChange={(value) => onChange({ maxItemsPerReturnRequest: value })}
            />
            <UnitInput
              label="Max returns per customer · 30 days"
              value={settings.maxReturnsPerCustomerPerMonth}
              onChange={(value) => onChange({ maxReturnsPerCustomerPerMonth: value })}
            />
          </div>
        </PolicyRow>

        <PolicyRow title="Quality control" description="Inspect items at the courier pickup">
          <div className="sm:self-end">
            <Toggle checked={settings.qcEnabled} onChange={(checked) => onChange({ qcEnabled: checked })} />
          </div>
        </PolicyRow>

        <PolicyRow
          title="Auto-block abusive customers"
          description="Automatically block customers who exceed the return threshold."
        >
          <div className="flex flex-col gap-3 sm:items-end">
            <Toggle
              checked={settings.autoBlockEnabled}
              onChange={(checked) => onChange({ autoBlockEnabled: checked })}
            />
            {settings.autoBlockEnabled ? (
              <div className="w-full sm:w-40">
                <UnitInput
                  label="Threshold · 30 days"
                  value={settings.autoBlockThreshold}
                  onChange={(value) => onChange({ autoBlockThreshold: value })}
                />
              </div>
            ) : null}
          </div>
        </PolicyRow>
      </PolicyCard>

      <PolicyCard
        title="Green returns (returnless refunds)"
        description="For low-value items, refund without asking the customer to ship anything back."
      >
        <PolicyRow title="Enable green returns" description="Reduces ops cost on items where shipping back costs more than the item.">
          <div className="sm:self-end">
            <Toggle
              checked={settings.greenReturnsEnabled}
              onChange={(checked) => onChange({ greenReturnsEnabled: checked })}
            />
          </div>
        </PolicyRow>

        {settings.greenReturnsEnabled ? (
          <>
            <PolicyRow title="Max value" description="Above this, customer must ship the item back.">
              <div className="w-full sm:w-48">
                <UnitInput
                  prefix="₹"
                  value={settings.greenReturnsThreshold}
                  onChange={(value) => onChange({ greenReturnsThreshold: value })}
                />
              </div>
            </PolicyRow>

            <PolicyRow title="Excluded reasons" description="Green returns never apply to these reasons.">
              <ReasonMultiSelect
                values={settings.greenReturnsExcludedReasons ?? []}
                onChange={(value) => onChange({ greenReturnsExcludedReasons: value })}
              />
            </PolicyRow>
          </>
        ) : null}
      </PolicyCard>
    </div>
  );
}
